import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IWatchlistCompany, IBenchmarkCompany } from '../../monitoring.model';
import { MonitoringService } from '../../monitoring.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NgxPaginationModule } from 'ngx-pagination';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { REGION_CODES } from 'public/country-region-codes';
import { Store } from '@ngrx/store';
import { submitSearch } from 'src/app/state/action';
import { AppState } from 'src/app/state/app.state';
import { NullableNumberPipe } from 'src/app/shared/pipes/nullable-number.pipe';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as XLSX from 'xlsx';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
// import { NotFoundError } from 'rxjs';
@Component({
  selector: 'app-watchlist-companies-table',
  standalone: true,
  imports: [CommonModule, NgxPaginationModule, ClipboardModule, NullableNumberPipe, MatTooltipModule, ReactiveFormsModule, FormsModule],
  templateUrl: './watchlist-companies-table.component.html',
  styleUrl: './watchlist-companies-table.component.css'
})
export class WatchlistCompaniesTableComponent implements OnChanges {
  @Input() companies: (IWatchlistCompany | IBenchmarkCompany)[] = [];
  @Input() loading: boolean = false;
  @Input() isReadOnly: boolean = false;
  @Output() updatedWatchlist = new EventEmitter<void>();
  constructor(private monitoringService: MonitoringService, private snackBar: MatSnackBar, private store: Store<AppState>, private dialog: MatDialog) {

  }
  searchQuery: string = '';

  filteredCompanyList: (IWatchlistCompany | IBenchmarkCompany)[] = [];
  countriesControl = new FormControl<string[]>([]);
  expandedRegions: Set<string> = new Set();

  countryFilterOptions = REGION_CODES;
  gicsSubindustryOptions: string[] = [];

  // Sorting state
  sortColumn: string = 'innovation_score';
  sortDirection: 'asc' | 'desc' = 'desc';


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companies']) {
      // console.log('companies changed');
      //reset filters and pagination
      this.countriesControl.setValue([]);
      this.gicsSubindustryControl.setValue([]);
      this.paginator_page = 1;
      this.searchQuery = '';
      this.sortColumn = 'innovation_score';
      this.sortDirection = 'desc';
      this.expandedRegions = new Set();
      this.processCompanies(this.companies);

    }
  }

  processCompanies(companies: (IWatchlistCompany | IBenchmarkCompany)[]) {
    this.filteredCompanyList = companies;
    this.updateGicsSubindustryOptions(companies);
    this.applyFilters();
  }

  updateGicsSubindustryOptions(companies: (IWatchlistCompany | IBenchmarkCompany)[]) {
    const uniqueSubindustries = new Set<string>();
    companies.forEach(company => {
      const subindustryName = company.gics_custom_name || company.gsubind_extended_name || company.subindustryname;
      if (subindustryName) {
        uniqueSubindustries.add(subindustryName);
      }
    });
    this.gicsSubindustryOptions = Array.from(uniqueSubindustries).sort();
  }

  getBarColor(score: number | undefined): string {
    if (score! >= 90) return 'green';
    if (score! >= 50) return 'orange';
    return 'red';
  }
  getOrdinalSuffix(rank: number | undefined) {
    if (!rank) return '';
    if (rank % 10 === 1 && rank % 100 !== 11) return 'st';
    if (rank % 10 === 2 && rank % 100 !== 12) return 'nd';
    if (rank % 10 === 3 && rank % 100 !== 13) return 'rd';
    return 'th';
  }

  // filtering for gics subindustry
  gicsSubindustryControl = new FormControl<string[]>([]);



  applyFilters(): void {
    this.filteredCompanyList = this.companies;

    if (this.countriesControl.value?.length) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        this.countriesControl.value!.includes(company.country!)
      );
    }
    if (this.gicsSubindustryControl.value?.length) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) => {
        const subindustryName = company.gics_custom_name || company.gsubind_extended_name || company.subindustryname;
        return subindustryName && this.gicsSubindustryControl.value!.includes(subindustryName);
      });
    }
    if (this.searchQuery) {
      this.filteredCompanyList = this.filteredCompanyList.filter((company) =>
        company.entity_name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }

    this.applySorting();
  }
  newSearch(company: IWatchlistCompany | IBenchmarkCompany) {
    if (!company.gsubind) {
      company.gsubind = company.gsubind_extended;
    }
    company.companytype = company.companytype || 'Public Company';
    const searchParams = { 'companies': [company] };

    this.store.dispatch(submitSearch({ searchParams, searchType: 'company' }));
  }

  deleteFromWatchlist(company: IWatchlistCompany | IBenchmarkCompany) {
    // Only allow deletion for watchlists, not benchmarks (read-only)
    if ('benchmark_id' in company) {
      this.snackBar.open('Cannot delete companies from benchmark portfolios', 'Close', { duration: 3000 });
      return;
    }

    ConfirmationDialogComponent.confirmDelete(this.dialog, company.entity_name).subscribe(confirmed => {
      if (confirmed) {
        this.monitoringService.removeCompanyFromWatchlist(company.watchlist_id, company.entityid).subscribe((res) => {
          if (res.message) {
            this.snackBar.open(res.message, 'Close', { duration: 3000 });
            this.updatedWatchlist.emit();
          }
        });
      }
    });
  }

  toggleExpand(region: string): void {
    if (this.expandedRegions.has(region)) {
      this.expandedRegions.delete(region);
    } else {
      this.expandedRegions.add(region);
    }
  }

  isExpanded(region: string): boolean {
    return this.expandedRegions.has(region);
  }

  toggleRegionSelection(region: any): void {
    const allSelected = this.isRegionFullySelected(region);
    const regionCountryCodes = region.countries.map((c: any) => c.code);

    if (allSelected) {
      const updatedSelection = (this.countriesControl.value ?? []).filter(
        (value: string) => !regionCountryCodes.includes(value)
      );
      this.countriesControl.setValue(updatedSelection);
    } else {
      const updatedSelection = Array.from(
        new Set([...(this.countriesControl.value ?? []), ...regionCountryCodes])
      );
      this.countriesControl.setValue(updatedSelection);
    }
    this.applyFilters();
  }

  toggleCountrySelection(countryCode: string, region: any): void {
    const currentSelection = this.countriesControl.value ?? [];
    const isSelected = currentSelection.includes(countryCode);

    if (isSelected) {
      this.countriesControl.setValue(
        currentSelection.filter((value: string) => value !== countryCode)
      );
    } else {
      this.countriesControl.setValue([...currentSelection, countryCode]);
    }
    this.applyFilters();
  }

  isRegionFullySelected(region: any): boolean {
    const regionCountryCodes = region.countries.map((c: any) => c.code);
    const selectedCodes = this.countriesControl.value ?? [];
    return regionCountryCodes.every((code: string) => selectedCodes.includes(code));
  }

  toggleGicsSubindustrySelection(subindustryName: string): void {
    const currentSelection = this.gicsSubindustryControl.value ?? [];
    const isSelected = currentSelection.includes(subindustryName);

    if (isSelected) {
      this.gicsSubindustryControl.setValue(
        currentSelection.filter((value: string) => value !== subindustryName)
      );
    } else {
      this.gicsSubindustryControl.setValue([...currentSelection, subindustryName]);
    }
    this.applyFilters();
  }

  sort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.applySorting();
  }

  applySorting(): void {
    if (!this.sortColumn) return;
    this.paginator_page = 1;
    this.filteredCompanyList.sort((a, b) => {
      let aVal: number = 0, bVal: number = 0;

      switch (this.sortColumn) {
        case 'innovation_score':
          aVal = a.innovation_score || 0;
          bVal = b.innovation_score || 0;
          break;
        case 'score_change':
          aVal = a.innovation_score_change || 0;
          bVal = b.innovation_score_change || 0;
          break;
        case 'rank_change':
          aVal = a.pct_rank_climbed || 0;
          bVal = b.pct_rank_climbed || 0;
          break;
      }

      return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'bi-arrow-down-up';
    return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
  }

  paginator_page: number = 1;


  trackByEntityId(index: number, company: IWatchlistCompany | IBenchmarkCompany): string {
    return company.entityid;
  }
  getIndustryLevel(level: number | string | undefined) {
    //sub-industry 4 / industry 3/ sub-sector 2/ sector1
    switch (level) {
      case '1':
      case 1:
        return 'Sector';
      case '2':
      case 2:
        return 'Sub-Sector';
      case '3':
      case 3:
        return 'Industry';
      case '4':
      case 4:
        return 'Sub-Industry';
      default:
        return 'Unknown';
    }
  }

  // Simple bracket to color mapping (1-5 from backend)
  getBracketColor(bracket: number | undefined): string {
    if (!bracket) return 'gray';
    // const colors = ['dark-red', 'light-red', 'gray', 'light-green', 'dark-green'];
    const colors = ['dark-red', 'dark-red', 'gray', 'dark-green', 'dark-green'];
    return colors[bracket - 1] || 'gray';
  }

  // Simple bracket to arrow mapping using Bootstrap chevron icons
  getBracketArrow(bracket: number | undefined): string {
    if (!bracket) return '';
    if (bracket === 1) return 'bi-chevron-double-down';
    if (bracket === 2) return 'bi-chevron-down';
    if (bracket === 4) return 'bi-chevron-up';
    if (bracket === 5) return 'bi-chevron-double-up';
    return ''; // Gray - no arrows
  }

  // Simple tooltip messages for each bracket
  getBracketTooltip(bracket: number | undefined) {
    if (!bracket) return '';
    if (bracket === 1) return 'Among the bottom 5% globally.';
    if (bracket === 2) return 'Among the bottom 10% globally.';
    if (bracket === 3) return null;
    if (bracket === 4) return 'Among the top 10% globally.';
    if (bracket === 5) return 'Among the top 5% globally.';
    return null;
  }

  formatRankInPeerGroup(rank: number | undefined, peerGroupSize: number | undefined) {
    if (!rank || !peerGroupSize) return null;
    const suffix = this.getOrdinalSuffix(rank);
    return `${rank}${suffix} (out of ${peerGroupSize})`;
  }

  getScorePercentage(score: number | undefined) {
    return score ? Math.min((score / 100) * 100, 100) : 0;
  }

  getBracketText(bracket: number | undefined): string {
    if (bracket === 1) return 'Severe Red Flag (Bottom 5%)';
    if (bracket === 2) return 'Red Flag (Bottom 10%)';
    if (bracket === 3) return 'None';
    if (bracket === 4) return 'Green Flag (Top 10%)';
    if (bracket === 5) return 'Strong Green Flag (Top 5%)';
    return '';
  }

  downloadExcel() {
    const list = this.companies.map(item => {

      return {
        Company: item.entity_name,
        ISIN: item.isin,
        Country: item.country,
        GICS_Peer_Group: item.gics_custom_name || item.gsubind_extended_name || item.subindustryname,
        Innovation_Score: item.innovation_score ? Math.round(item.innovation_score * 10) / 10 : item.innovation_score,
        Score_Change_3_Months: item.innovation_score_change ? Math.round(item.innovation_score_change * 10) / 10 : item.innovation_score_change,
        //format the rank as a number with the suffix
        Rank_in_Peer_Group: item.rank ? `${item.rank}${this.getOrdinalSuffix(item.rank)} (out of ${item.peer_group_size})` : '',
        Rank_Climb_3_Months: item.rank_climbed,
        Percentile_Rank_Climb_3_Months: item.pct_rank_climbed ? Math.round(item.pct_rank_climbed * 10) / 10 : item.pct_rank_climbed,
        Score_Change_Warning: this.getBracketText(item.score_bracket),
        Rank_Change_Warning: this.getBracketText(item.rank_bracket)
      };
    });

    const ws = XLSX.utils.json_to_sheet(list);
    const wb = XLSX.utils.book_new();


    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 25 }, { wch: 15 },
      { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 25 }, { wch: 25 }
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'watchlist_companies');
    const firstCompany = this.companies[0];
    let fileName = 'portfolio_companies.xlsx';
    if (firstCompany) {
      if ('watchlist_name' in firstCompany && firstCompany.watchlist_name) {
        fileName = `${firstCompany.watchlist_name}.xlsx`;
      } else if ('benchmark_name' in firstCompany && firstCompany.benchmark_name) {
        fileName = `${firstCompany.benchmark_name}.xlsx`;
      }
    }
    XLSX.writeFile(wb, fileName);

  }


}